import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useCreateContact, useUpdateContact } from '@/hooks/useCrm';
import { extractError } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';
import type { Contact, ContactType } from '@/types';

interface Props {
  open:    boolean;
  onClose: () => void;
  /** TICKET-004 (C-01) : fourni = modification de cette fiche ; absent = création. */
  contact?: Contact | null;
  onSaved?: (c: Contact) => void;
}

const empty = () => ({
  contactType: 'client' as ContactType,
  fullName:    '',
  companyName: '',
  email:       '',
  phone:       '',
  address:     '',
  pays:        'Côte d\'Ivoire',
  // Ticket SIMGROUP-2026-003 §3.2 — mentions légales client
  rccmClient:  '',
  nccClient:   '',
});

const versForm = (c: Contact) => ({
  contactType: c.contactType,
  fullName:    c.fullName,
  companyName: c.companyName ?? '',
  email:       c.email ?? '',
  phone:       c.phone ?? '',
  address:     c.address ?? '',
  pays:        c.pays ?? '',
  rccmClient:  c.rccmClient ?? '',
  nccClient:   c.nccClient ?? '',
});

const TYPE_LABELS: Record<ContactType, string> = {
  client:   'Client',
  supplier: 'Fournisseur',
  prospect: 'Prospect',
};

/** Types qu'un rôle gère (même règle que le serveur : contacts.rules.ts). */
/**
 * Types qu'un rôle peut modifier — EXACTEMENT la règle du serveur (apps/api/src/modules/crm/contacts.rules.ts).
 * 31/10/2026 : l'écran lisait la table des droits d'affichage, où le DAF n'a pas « crm.prospects.manage » ; le bouton
 * « Modifier » manquait donc sur les prospects (TG2T) alors que le serveur l'autorise — un prospect ne pouvait jamais
 * devenir client.
 */
export const typesGerables = (role: string | undefined): ContactType[] => {
  if (role === 'acheteur') return ['supplier'];
  if (role === 'commercial') return ['client', 'prospect'];
  if (['daf', 'dg_filiale', 'manager', 'admin_holding'].includes(role ?? '')) return ['client', 'prospect', 'supplier'];
  return [];
};

export const ContactFormDialog = ({ open, onClose, contact, onSaved }: Props) => {
  const role = useAuthStore((s) => s.user?.role);
  const permis = typesGerables(role);
  const edition = Boolean(contact);
  const [form, setForm] = useState(empty());
  const [error, setError] = useState<string | null>(null);
  const create = useCreateContact();
  const update = useUpdateContact();

  useEffect(() => {
    if (!open) return;
    const f = contact ? versForm(contact) : empty();
    if (!contact && permis.length && !permis.includes(f.contactType)) f.contactType = permis[0];
    setForm(f);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contact?.id]);

  // En modification, le type actuel reste affiché même s'il sort du périmètre du rôle (le serveur refusera).
  const typesProposes = Array.from(new Set([...(edition ? [form.contactType] : []), ...permis]));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      if (contact) {
        // Tous les champs sont envoyés : un champ vidé est enregistré vide.
        const c = await update.mutateAsync({ id: contact.id, payload: {
          contactType: form.contactType,
          fullName:    form.fullName.trim(),
          companyName: form.companyName.trim(),
          email:       form.email.trim(),
          phone:       form.phone.trim(),
          address:     form.address.trim(),
          pays:        form.pays.trim(),
          rccmClient:  form.rccmClient.trim(),
          nccClient:   form.nccClient.trim(),
        } });
        onSaved?.(c);
      } else {
        const c = await create.mutateAsync({
          contactType: form.contactType,
          fullName:    form.fullName.trim(),
          companyName: form.companyName.trim() || undefined,
          email:       form.email.trim() || undefined,
          phone:       form.phone.trim() || undefined,
          address:     form.address.trim() || undefined,
          pays:        form.pays.trim() || undefined,
          rccmClient:  form.rccmClient.trim() || undefined,
          nccClient:   form.nccClient.trim() || undefined,
        });
        onSaved?.(c);
      }
      onClose();
    } catch (err) {
      setError(extractError(err).message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={edition ? `Modifier — ${contact!.companyName ?? contact!.fullName}` : 'Nouveau contact'}
      subtitle={edition ? 'Les factures déjà émises gardent les mentions du jour de leur émission' : 'Client, fournisseur ou prospect'}
      size="md"
    >
      <form onSubmit={onSubmit} className="space-y-3">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Type *">
            <select
              value={form.contactType}
              onChange={(e) => setForm((p) => ({ ...p, contactType: e.target.value as ContactType }))}
              className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
            >
              {typesProposes.map((t) => (
                <option key={t} value={t}>{TYPE_LABELS[t]}</option>
              ))}
            </select>
          </Field>
          <Field label="Pays">
            <Input value={form.pays} onChange={(e) => setForm((p) => ({ ...p, pays: e.target.value }))} />
          </Field>
        </div>

        <Field label="Société (raison sociale)">
          <Input value={form.companyName} onChange={(e) => setForm((p) => ({ ...p, companyName: e.target.value }))} placeholder="Kouassi BTP" />
        </Field>

        <Field label="Nom complet contact *">
          <Input value={form.fullName} onChange={(e) => setForm((p) => ({ ...p, fullName: e.target.value }))} required placeholder="Kouassi Brou Jean-Marc" />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Email">
            <Input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
          </Field>
          <Field label="Téléphone">
            <Input value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} placeholder="+225 07..." />
          </Field>
        </div>

        <Field label="Adresse">
          <Input value={form.address} onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))} placeholder="Cocody Angré, 22e Arrondissement, Lot 276..." />
        </Field>

        {/* Ticket SIMGROUP-2026-003 §3.2 — Mentions légales OHADA client (aussi pour un prospect, futur client) */}
        {form.contactType !== 'supplier' && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
            <div className="mb-2 text-xs font-semibold text-amber-900">
              Mentions légales OHADA (client B2B)
            </div>
            <div className="mb-2 text-[11px] text-amber-800">
              Obligatoires pour émettre une facture. Elles peuvent être saisies plus tard, depuis ce même formulaire.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="RCCM client">
                <Input value={form.rccmClient} onChange={(e) => setForm((p) => ({ ...p, rccmClient: e.target.value }))} placeholder="CI-ABJ-01-2023-B12-01234" />
              </Field>
              <Field label="N° Compte Contribuable (NCC)">
                <Input value={form.nccClient} onChange={(e) => setForm((p) => ({ ...p, nccClient: e.target.value }))} placeholder="1234567 A" />
              </Field>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <Button type="button" variant="secondary" onClick={onClose}>Annuler</Button>
          <Button type="submit" loading={create.isPending || update.isPending}>{edition ? 'Enregistrer' : 'Créer le contact'}</Button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>
    {children}
  </div>
);
